import PortalLayout from '@/components/layout/portal/PortalLayout';
import IdleModal from '@/components/modal/IdleModal';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import { Fragment, useEffect, useMemo, useState } from 'react';

import PortalInternalTransferModal from '@/components/modal/PortalInternalTransferModal';
import PortalWithdrawalModal from '@/components/modal/PortalWithdrawalModal';
import PortalBalancesTable from '@/components/portal-modules/PortalBalancesTable';
import PortalWithdrawalTable from '@/components/portal-modules/PortalWithdrawalTable';
import { formatToDate, getOperationalCurrentTradeDate } from '@/helpers/dateHelper';
import { selectTablePagination } from '@/state/reducers/tableSettingSlice';
import { DepositWithdrawalRequestHistoryReturn } from '@/types/sso';
import Unauthorized401 from '@/Unauthorized401';
import { getCurrency } from '@/utils/hooks/useCurrency';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalAuthentication from '@/utils/hooks/usePortalAuthentication';
import useScreenSizes from '@/utils/hooks/useScreenSizes';
import { fetchPortalAPI, usePortalSWR, useSso } from '@/utils/providers/SSOProvider';
import { subWeeks } from 'date-fns';
import { MdOutlineAccountBalance, MdOutlineAccountBalanceWallet, MdOutlineSwapHoriz } from 'react-icons/md';
import Skeleton from 'react-loading-skeleton';
import { useSearchParams } from 'react-router-dom';
import { DepositWithdrawalCard, DepositWithdrawalRequestFilters } from './PortalDeposit';

const cardHeight = 120;
const tableHeight = 204.2;

const currentTradeDate = getOperationalCurrentTradeDate();

function PortalWithdrawal() {
    usePortalAuthentication();

    const lang = useLanguage();
    const query = useSearchParams();
    const screen = useScreenSizes();
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);
    const pagination = useAppSelector(selectTablePagination);

    const idleDisclosure = useDisclosure(false);
    const portalWithdrawalDisclosure = useDisclosure(false);
    const portalInternalTransferDisclosure = useDisclosure(false);

    const [withdrawalCcy, setWithdrawalCcy] = useState('');
    const [withdrawalMode, setWithdrawalMode] = useState<'bank' | 'crypto'>('bank');
    const [withdrawalPagination, setWithdrawalPagination] = useState({ pageSize: pagination.pageSize });

    const { jwt, foundEntity, summaryApi, setSummaryHistoricDate, hasPermission, onFetchJWT } = useSso();
    const { data: summary } = summaryApi;

    const { balances } = useMemo(() => {
        if (!summary) return { balances: undefined };
        return {
            balances: summary.balancesSummary
        };
    }, [summary]);

    const defaultFilters = useMemo(
        () => ({
            requestStatus: {
                value: 'All' as const,
                label: lang.common.requestStatus
            },
            startDate: {
                value: subWeeks(currentTradeDate, 1),
                label: lang.common.startDate
            },
            endDate: {
                value: currentTradeDate,
                label: lang.common.endDate
            }
        }),
        [lang, currentTradeDate]
    );

    const [offset, setOffset] = useState<number>(0);
    const [isInitialMount, setIsInitialMount] = useState<boolean>(true);
    const [filters, setFilters] = useState<DepositWithdrawalRequestFilters>(defaultFilters);

    const requestPayload = useMemo(
        () => ({
            entityId: foundEntity?.entityId.toString(),
            fromDate: formatToDate(filters.startDate.value),
            toDate: formatToDate(filters.endDate.value),
            type: 'Withdrawal,Internal-Transfer-Withdrawal',
            limit: withdrawalPagination.pageSize,
            offset,
            status: filters.requestStatus.value !== 'All' ? filters.requestStatus.value : undefined
        }),
        [foundEntity?.entityId, withdrawalPagination.pageSize, offset, filters]
    );

    const withdrawalsApi = usePortalSWR<DepositWithdrawalRequestHistoryReturn>(
        '/api/requests/history-withdrawals',
        () => fetchPortalAPI(onFetchJWT, '/api/requests/history', jwt, requestPayload, 'POST')
    );
    const { data: withdrawalRequests } = withdrawalsApi;

    useEffect(() => {
        withdrawalsApi.mutate();
    }, [filters.requestStatus, filters.startDate, filters.endDate, offset, withdrawalPagination.pageSize]);

    useEffect(() => {
        withdrawalsApi.mutate().then(() => {
            setIsInitialMount(false);
        });
    }, [currentAccount]);

    useEffect(() => {
        summaryApi.mutate();
        setSummaryHistoricDate(undefined);
    }, [currentAccount]);

    useEffect(() => {
        const currency = query[0].get('currency');
        if (currency) {
            const config = getCurrency(currency);
            portalWithdrawalDisclosure[1].open();
            setWithdrawalMode(config.type === 'Crypto' ? 'crypto' : 'bank');
            setWithdrawalCcy(config.show);
        }
    }, []);

    const isAuthorized = hasPermission('REQUEST_WITHDRAWAL');

    return credentials ? (
        <PortalLayout isAuthorized={isAuthorized}>
            {isAuthorized && (
                <Fragment>
                    <div className="flex flex-col w-full gap-2 sm:gap-4 p-2 sm:p-6 text-brand-neutral-200 mt-16 lg:mt-0">
                        <div className="flex flex-wrap md:h-[34px] gap-2 w-full justify-between">
                            <div className="font-bold text-xl">{lang.navigation.withdrawals}</div>
                        </div>
                        {isInitialMount && (withdrawalsApi.isLoading || withdrawalsApi.isValidating) ? (
                            <Fragment>
                                {screen.mobile ? (
                                    <div className="flex flex-col w-full gap-2 sm:gap-4">
                                        <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        <Skeleton containerClassName="flex-1" height={tableHeight} />
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap w-full gap-2 sm:gap-4">
                                        <div className="flex w-full relative">
                                            <Skeleton containerClassName="flex-1" height={tableHeight} />
                                            <div
                                                className="absolute top-[57px] left-[25px] flex flex-wrap w-full max-w-screen-lg gap-2 sm:gap-4"
                                                style={{
                                                    width: `calc(100% - 50px)`
                                                }}>
                                                <Skeleton containerClassName="flex-1" height={cardHeight} />
                                                <Skeleton containerClassName="flex-1" height={cardHeight} />
                                                <Skeleton containerClassName="flex-1" height={cardHeight} />
                                            </div>
                                        </div>
                                        <div className="flex w-full">
                                            <Skeleton containerClassName="flex-1" height={tableHeight} />
                                        </div>
                                    </div>
                                )}
                            </Fragment>
                        ) : (
                            <Fragment>
                                <div className="flex flex-col gap-4 bg-brand-background-dark p-4 sm:p-6 py-4 border border-brand-neutral-700 text-brand-neutral-200 rounded-md">
                                    <div className="">{lang.portal.requestAWithdrawal}</div>
                                    <div className="flex flex-wrap w-full gap-2 sm:gap-4 max-w-screen-lg">
                                        <DepositWithdrawalCard
                                            Icon={MdOutlineAccountBalance}
                                            title={lang.portal.bankAccountWithdrawal}
                                            description={lang.portal.bankAccountWithdrawalDescription}
                                            onClick={() => {
                                                setWithdrawalMode('bank');
                                                portalWithdrawalDisclosure[1].open();
                                            }}
                                            disabled={!hasPermission('REQUEST_WITHDRAWAL')}
                                        />
                                        {foundEntity?.attributes?.includes('DELIVERABLE') && (
                                            <DepositWithdrawalCard
                                                Icon={MdOutlineAccountBalanceWallet}
                                                title={lang.portal.cryptoWallet}
                                                description={lang.portal.cryptoWalletDescription}
                                                onClick={() => {
                                                    setWithdrawalMode('crypto');
                                                    portalWithdrawalDisclosure[1].open();
                                                }}
                                                disabled={!hasPermission('REQUEST_WITHDRAWAL')}
                                            />
                                        )}
                                        <DepositWithdrawalCard
                                            Icon={MdOutlineSwapHoriz}
                                            title={lang.portal.internalTransfer}
                                            description={lang.portal.internalTransferDescription}
                                            onClick={portalInternalTransferDisclosure[1].open}
                                            disabled={
                                                !hasPermission(['REQUEST_DEPOSIT', 'REQUEST_WITHDRAWAL']) ||
                                                credentials?.accounts?.length <= 1
                                            }
                                        />
                                    </div>
                                </div>
                                <PortalWithdrawalTable
                                    data={withdrawalRequests}
                                    filters={filters}
                                    pagination={withdrawalPagination}
                                    setPagination={setWithdrawalPagination}
                                    setFilters={setFilters}
                                    setOffset={setOffset}
                                    defaultFilters={defaultFilters}
                                    isLoading={withdrawalsApi.isLoading || withdrawalsApi.isValidating}
                                    mutate={withdrawalsApi.mutate}
                                />
                                {summaryApi.isLoading || summaryApi.isValidating ? (
                                    <div className="flex w-full">
                                        <Skeleton containerClassName="flex-1" height={tableHeight} />
                                    </div>
                                ) : (
                                    <PortalBalancesTable
                                        balances={balances}
                                        baseCurrency={balances?.baseCurrency || ''}
                                        onWithdraw={(currency) => {
                                            if (currency) {
                                                const config = getCurrency(currency);
                                                portalWithdrawalDisclosure[1].open();
                                                setWithdrawalMode(config.type === 'Crypto' ? 'crypto' : 'bank');
                                                setWithdrawalCcy(config.show);
                                            }
                                        }}
                                        showRefresh
                                    />
                                )}
                            </Fragment>
                        )}
                    </div>
                    <IdleModal opened={idleDisclosure[0]} handlers={idleDisclosure[1]} />
                    <PortalWithdrawalModal
                        mode={withdrawalMode}
                        currency={withdrawalCcy}
                        opened={portalWithdrawalDisclosure[0]}
                        handlers={portalWithdrawalDisclosure[1]}
                        onRefresh={() => {
                            withdrawalsApi.mutate();
                            summaryApi.mutate();
                        }}
                    />
                    <PortalInternalTransferModal
                        opened={portalInternalTransferDisclosure[0]}
                        handlers={portalInternalTransferDisclosure[1]}
                        onRefresh={() => {
                            withdrawalsApi.mutate();
                            summaryApi.mutate();
                        }}
                    />
                </Fragment>
            )}
            {!isAuthorized && <Unauthorized401 />}
        </PortalLayout>
    ) : null;
}

export default PortalWithdrawal;
